/**
 * Validation utility functions for form inputs
 */

/**
 * Validates email format
 * @param {string} email - Email address to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateEmail = (email) => {
  if (!email || email.trim() === '') {
    return { isValid: false, error: 'Email is required' };
  }

  // RFC 5322 compliant email regex (simplified)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }

  if (email.length > 254) {
    return { isValid: false, error: 'Email is too long' };
  }

  return { isValid: true, error: '' };
};

/**
 * Validates password strength
 * @param {string} password - Password to validate
 * @returns {Object} { isValid: boolean, error: string, strength: string }
 */
export const validatePassword = (password) => {
  if (!password || password.trim() === '') {
    return { isValid: false, error: 'Password is required', strength: 'none' };
  }

  if (password.length < 8) {
    return {
      isValid: false,
      error: 'Password must be at least 8 characters long',
      strength: 'weak'
    };
  }

  if (password.length > 128) {
    return {
      isValid: false,
      error: 'Password is too long (max 128 characters)',
      strength: 'invalid'
    };
  }

  // Check for password strength
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  const strengthScore = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar].filter(Boolean).length;

  let strength = 'weak';
  let error = '';

  if (strengthScore < 2) {
    strength = 'weak';
    error = 'Password should contain uppercase, lowercase, numbers, and special characters';
  } else if (strengthScore === 2) {
    strength = 'medium';
  } else if (strengthScore === 3) {
    strength = 'strong';
  } else {
    strength = 'very strong';
  }

  // Minimum requirements: at least 8 chars + at least 2 different character types
  const meetsMinimum = password.length >= 8 && strengthScore >= 2;

  return {
    isValid: meetsMinimum,
    error: meetsMinimum ? '' : error,
    strength
  };
};

/**
 * Validates username
 * @param {string} userName - Username to validate
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateUserName = (userName) => {
  if (!userName || userName.trim() === '') {
    return { isValid: false, error: 'Username is required' };
  }

  if (userName.length < 3) {
    return { isValid: false, error: 'Username must be at least 3 characters long' };
  }

  if (userName.length > 30) {
    return { isValid: false, error: 'Username is too long (max 30 characters)' };
  }

  // Allow alphanumeric, underscore, hyphen
  const userNameRegex = /^[a-zA-Z0-9_-]+$/;

  if (!userNameRegex.test(userName)) {
    return {
      isValid: false,
      error: 'Username can only contain letters, numbers, underscores, and hyphens'
    };
  }

  return { isValid: true, error: '' };
};

/**
 * Validates password confirmation match
 * @param {string} password - Original password
 * @param {string} confirmPassword - Confirmation password
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validatePasswordMatch = (password, confirmPassword) => {
  if (!confirmPassword || confirmPassword.trim() === '') {
    return { isValid: false, error: 'Please confirm your password' };
  }

  if (password !== confirmPassword) {
    return { isValid: false, error: 'Passwords do not match' };
  }

  return { isValid: true, error: '' };
};

/**
 * Sanitize input to prevent XSS
 * @param {string} input - Input string to sanitize
 * @returns {string} Sanitized string
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;

  // Remove any HTML tags and trim whitespace
  return input
    .replace(/<[^>]*>/g, '')
    .trim();
};

/**
 * Validates entire login form
 * @param {Object} formData - Form data object
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validateLoginForm = (formData) => {
  const errors = {};

  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
  }

  const passwordValidation = validatePassword(formData.password);
  if (!passwordValidation.isValid) {
    errors.password = passwordValidation.error;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Validates entire signup form
 * @param {Object} formData - Form data object
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validateSignupForm = (formData) => {
  const errors = {};

  const userNameValidation = validateUserName(formData.userName);
  if (!userNameValidation.isValid) {
    errors.userName = userNameValidation.error;
  }

  const emailValidation = validateEmail(formData.email);
  if (!emailValidation.isValid) {
    errors.email = emailValidation.error;
  }

  const passwordValidation = validatePassword(formData.password);
  if (!passwordValidation.isValid) {
    errors.password = passwordValidation.error;
  }

  const passwordMatchValidation = validatePasswordMatch(
    formData.password,
    formData.confirmPassword
  );
  if (!passwordMatchValidation.isValid) {
    errors.confirmPassword = passwordMatchValidation.error;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
